import React from 'react';
import PropTypes from 'prop-types';

import { useSelector, useDispatch } from 'react-redux';

import {
  selectHazardChartData,
  selectHazardCsvData,
  selectLocationEpisodes,
} from '../../../store/data/data.selector';
import { selectCurrentLocation } from '../../../store/map/map.selector';
import { getEpisodesDataAsync } from '../../../store/episodes/episodes.slice';
import { changeEpisodeTimelineVisible } from '../../../store/ui/ui.slice';

import { getAttributeText } from '../../../utilities/mapPopupText';
import downloadData from '../../../utilities/downloadData';

import {
  selectAttributeInfo,
  selectDateRange,
  selectHazardMetric,
} from '../../../store/options/options.selector';

import formatNumber from '../../../utilities/formatNumber';

export default function HazardChart({
  HighchartsReact,
  Highcharts,
  defaultOptions,
}) {
  const dispatch = useDispatch();
  const data = useSelector(selectHazardChartData);
  const csvData = useSelector(selectHazardCsvData);
  const loc = useSelector(selectCurrentLocation);
  const metric = useSelector(selectHazardMetric);
  const attrInfo = useSelector(selectAttributeInfo);
  const dateRange = useSelector(selectDateRange);
  const locEpisodes = useSelector(selectLocationEpisodes);

  if (attrInfo === undefined) return '';

  let { attrName, unit } = getAttributeText(false, metric);

  const options = {
    ...defaultOptions,
    exporting: {
      ...defaultOptions.exporting,
      chartOptions: {
        subtitle: '',
      },
      menuItemDefinitions: {
        downloadData: {
          onclick: () => downloadData(
            'hazard',
            loc,
            attrInfo,
            csvData,
            dateRange
          ),
          text: 'Download Chart Data',
        },
      },
    },
    legend: { enabled: false },
    chart: {
      ...defaultOptions,
      type: 'column',
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
    title: {
      ...defaultOptions.title,
      text: attrName
        ? `Annual ${
            metric === 'count'
              ? 'Reported'
              : `${attrName.split(' ').slice(2).join(' ')} from`
          } ${attrInfo.name} Events in ${
            loc.state ? `${loc.name} County, ${loc.state}` : loc.name
          } from ${dateRange.start} to ${dateRange.end}`
        : '',
    },
    subtitle: {
      text: 'Click bars for episode details',
    },
    series: [
      {
        data: data,
        name: attrName,
      },
    ],
    yAxis: {
      allowDecimals: false,
      endOnTick: false,
      tickAmount: 5,
      gridLineColor: '#a1a1a1',
      type: unit === '' ? 'logarithmic' : 'linear',
      title: {
        text: unit && unit !== '' ? attrName : 'USD',
      },
      labels: {
        formatter: function () {
          let value = this.value;

          if (value >= 1e15) {
            value = value / 1e15 + 'Qd';
          } else if (value >= 1e12) {
            value = value / 1e12 + 'T';
          } else if (value >= 1e9) {
            value = value / 1e9 + 'B';
          } else if (value >= 1e6) {
            value = value / 1e6 + 'M';
          } else if (value >= 1e3) {
            value = value / 1e3 + 'k';
          }

          if (unit === '') {
            return '$ ' + value;
          } else {
            return value;
          }
        },
      },
    },
    tooltip: {
      pointFormatter: function () {
        let value;
        if (unit === '') {
          value = '$ ' + formatNumber(this.y);
        } else {
          value = this.y;
        }
        return `${attrName}: ${value}`;
      },
      shared: true,
    },
    plotOptions: {
      column: {
        stacking: 'normal',
      },
      series: {
        cursor: 'pointer',
        events: {
          click: async function (event) {
            dispatch(
              getEpisodesDataAsync({
                type: 'changeYear',
                payload: locEpisodes.find((arr) => arr[0] === event.point.x),
              })
            );
            dispatch(changeEpisodeTimelineVisible(true));
          },
        },
      },
    },
    navigation: {
      ...defaultOptions.navigation,
      buttonOptions: {
        ...defaultOptions.navigation.buttonOptions,
        y: -5,
      },
    },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { height: '100%', width: '100%' } }}
    />
  );
}

HazardChart.propTypes = {
  HighchartsReact: PropTypes.object,
  Highcharts: PropTypes.object,
  defaultOptions: PropTypes.object,
};
